import React from 'react';
import { useSelector } from 'react-redux';

import RadioLiveView from '../../../Blocks/RadioLive/RadioLiveView';
import LatestNewsAside from '../../../shared/LatestNewsAside';
import UploadBannerSection from '../../../shared/UploadBannerSection/UploadBannerSection';
import PodcastEpisodesView from '../../../Blocks/PodcastEpisodes/PodcastEpisodesView';

import ScheduleGrid from './components/ScheduleGrid';
import ScheduleDayTabs from './components/ScheduleDayTabs';
import ScheduleStationSelect from './components/ScheduleStationSelect';

import { getStations } from '../../../../services/api/stations';
import { getPublicResolvedSchedule } from '../../../../services/api/radioSchedule';

import type {
  PublicResolvedSchedulePayload,
  ScheduleStationPayload,
} from '../../../../interfaces/schedule';

import './ScheduleView.less';

import bannerPremio from '../../../../theme/assets/images/banner-premio.png';

type DayOption = {
  id: string;
  label: string;
  date: string;
  dayNumber: string;
};

function formatIsoDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function getWeekdayShortLabel(date: Date) {
  const labels = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  return labels[date.getDay()];
}

function buildDayOptions(baseDate = new Date()): DayOption[] {
  return Array.from({ length: 14 }).map((_, index) => {
    const nextDate = new Date(baseDate);
    nextDate.setDate(baseDate.getDate() + (index - 1)); // hoje fica na 2ª posição

    return {
      id: formatIsoDate(nextDate),
      date: formatIsoDate(nextDate),
      label: getWeekdayShortLabel(nextDate),
      dayNumber: String(nextDate.getDate()).padStart(2, '0'),
    };
  });
}

function isToday(dateString: string) {
  return dateString === formatIsoDate(new Date());
}

function mapStationsToSchedulePayload(
  stations: Awaited<ReturnType<typeof getStations>>,
): ScheduleStationPayload[] {
  return stations.map((station) => ({
    id: station.id,
    title: station.title,
    shortTitle: station.shortName,
    isActive: true,
  }));
}

export default function ScheduleView() {
  const dayOptions = React.useMemo(() => buildDayOptions(), []);
  const dayTabsRef = React.useRef<HTMLDivElement | null>(null);
  const lastPlayerStationIdRef = React.useRef<string | null>(null);
  const autoSwitchTimerRef = React.useRef<number | null>(null);

  const playerStationId = useSelector(
    (state: any) => state.player?.currentStationId ?? null,
  );
  const isListeningLive = useSelector(
    (state: any) =>
      state.player?.mode !== 'vod' && !!state.player?.intentPlaying,
  );

  const [stations, setStations] = React.useState<ScheduleStationPayload[]>([]);
  const [activeStationId, setActiveStationId] = React.useState('');
  const [activeDate, setActiveDate] = React.useState(
    dayOptions.find((day) => isToday(day.date))?.date ??
      dayOptions[0]?.date ??
      '',
  );
  const [resolvedSchedule, setResolvedSchedule] =
    React.useState<PublicResolvedSchedulePayload | null>(null);
  const [loadingStations, setLoadingStations] = React.useState(true);
  const [loadingSchedule, setLoadingSchedule] = React.useState(false);
  const [error, setError] = React.useState('');
  const [autoSwitchNotice, setAutoSwitchNotice] = React.useState('');
  const [isFullScheduleOpen, setIsFullScheduleOpen] = React.useState(false);

  React.useEffect(
    () => () => {
      if (autoSwitchTimerRef.current) {
        window.clearTimeout(autoSwitchTimerRef.current);
      }
    },
    [],
  );

  React.useEffect(() => {
    let isMounted = true;

    async function loadStations() {
      try {
        setLoadingStations(true);
        const response = await getStations();

        if (!isMounted) return;

        const mappedStations = mapStationsToSchedulePayload(response);
        setStations(mappedStations);
      } catch {
        if (!isMounted) return;
        setError('Não foi possível carregar as emissoras.');
      } finally {
        if (isMounted) {
          setLoadingStations(false);
        }
      }
    }

    loadStations();

    return () => {
      isMounted = false;
    };
  }, []);

  React.useEffect(() => {
    if (!stations.length) return;

    const playingStation = isListeningLive
      ? stations.find((station) => station.id === playerStationId)
      : null;

    if (!activeStationId) {
      setActiveStationId(playingStation?.id ?? stations[0].id);
      lastPlayerStationIdRef.current = playingStation?.id ?? null;
      return;
    }

    if (!isListeningLive) {
      lastPlayerStationIdRef.current = null;
      return;
    }

    if (
      playingStation &&
      playingStation.id !== lastPlayerStationIdRef.current
    ) {
      lastPlayerStationIdRef.current = playingStation.id;
      setActiveStationId(playingStation.id);
      setAutoSwitchNotice(
        `Grade atualizada para ${playingStation.shortTitle || playingStation.title}`,
      );

      if (autoSwitchTimerRef.current) {
        window.clearTimeout(autoSwitchTimerRef.current);
      }

      autoSwitchTimerRef.current = window.setTimeout(() => {
        setAutoSwitchNotice('');
        autoSwitchTimerRef.current = null;
      }, 3200);
    }
  }, [activeStationId, isListeningLive, playerStationId, stations]);

  React.useEffect(() => {
    let isMounted = true;

    async function loadSchedule() {
      if (!activeStationId || !activeDate) return;

      try {
        setLoadingSchedule(true);
        setError('');

        const data = await getPublicResolvedSchedule({
          stationId: activeStationId,
          date: activeDate,
        });

        if (!isMounted) return;

        setResolvedSchedule(data);
      } catch {
        if (!isMounted) return;
        setResolvedSchedule(null);
        setError('Não foi possível carregar a programação.');
      } finally {
        if (isMounted) {
          setLoadingSchedule(false);
        }
      }
    }

    loadSchedule();

    return () => {
      isMounted = false;
    };
  }, [activeStationId, activeDate]);

  const activeStation =
    stations.find((station) => station.id === activeStationId) ??
    stations[0] ??
    null;
  const isFollowingLivePlayer =
    isListeningLive && playerStationId === activeStationId;

  return (
    <div className="schedule-view">
      <div className="schedule-view__top">
        <div
          className={`schedule-view__main${
            isFullScheduleOpen ? ' is-schedule-expanded' : ''
          }`}
        >
          <section
            className={`schedule-view__panel${
              autoSwitchNotice ? ' is-auto-switching' : ''
            }`}
          >
            <header className="schedule-view__header">
              <div className="schedule-view__header-left">
                <div className="schedule-view__eyebrow">
                  <span>Programação</span>
                  {isFollowingLivePlayer ? (
                    <span className="schedule-view__live-badge">
                      <span
                        className="schedule-view__live-dot"
                        aria-hidden="true"
                      />
                      Acompanhando o player
                    </span>
                  ) : (
                    <span className="schedule-view__station-badge">
                      Grade da emissora
                    </span>
                  )}
                </div>
                <h1 className="schedule-view__heading">
                  <span className="schedule-view__heading-station">
                    {activeStation?.shortTitle ||
                      activeStation?.title ||
                      'Rádio'}
                  </span>
                </h1>

                {resolvedSchedule?.currentItem ? (
                  <p className="schedule-view__now-playing">
                    Tocando agora:{' '}
                    <strong>{resolvedSchedule.currentItem.title}</strong>
                  </p>
                ) : null}

                <div
                  className={`schedule-view__switch-notice${
                    autoSwitchNotice ? ' is-visible' : ''
                  }`}
                  role="status"
                  aria-live="polite"
                >
                  {autoSwitchNotice}
                </div>
              </div>

              <div className="schedule-view__header-right">
                <ScheduleStationSelect
                  stations={stations}
                  activeStationId={activeStationId}
                  onChange={setActiveStationId}
                />
              </div>
            </header>

            <ScheduleDayTabs
              ref={dayTabsRef}
              days={dayOptions.map((day) => ({
                id: day.id,
                label: day.label,
                dayNumber: day.dayNumber,
              }))}
              activeDayId={activeDate}
              onChange={setActiveDate}
            />

            {loadingStations || loadingSchedule ? (
              <div className="schedule-view__empty">
                Carregando programação...
              </div>
            ) : null}

            {!loadingStations && !loadingSchedule && error ? (
              <div className="schedule-view__empty">{error}</div>
            ) : null}

            {!loadingStations &&
            !loadingSchedule &&
            !error &&
            resolvedSchedule?.items?.length ? (
              <ScheduleGrid
                title="Grade do dia"
                items={resolvedSchedule.items}
                activeStationId={activeStationId}
                stations={stations}
                showNowButton={isToday(activeDate)}
                enableNowPlayingHighlight={isToday(activeDate)}
                onChooseOtherStations={() => {
                  dayTabsRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                  });
                }}
                onSelectStation={setActiveStationId}
                onExpansionChange={setIsFullScheduleOpen}
              />
            ) : null}

            {!loadingStations &&
            !loadingSchedule &&
            !error &&
            !resolvedSchedule?.items?.length ? (
              <div className="schedule-view__empty">
                Nenhuma programação disponível para a data selecionada.
              </div>
            ) : null}
          </section>
        </div>

        <aside className="schedule-view__aside">
          <div className="schedule-view__aside-block">
            <RadioLiveView
              data={{
                variant: 'compact',
              }}
            />
          </div>

          <div className="schedule-view__aside-block">
            <LatestNewsAside />
          </div>

          <div className="schedule-view__aside-block">
            <UploadBannerSection
              imageUrl={bannerPremio}
              imageWidth={369}
              imageHeight={392}
              data={{
                imageAlt: '',
                href: '/programas/premio-radio-mec',
                title: 'Ir para página do Prêmio da Rádio MEC',
                linkAriaLabel: 'Acessar página do Prêmio da Rádio MEC',
              }}
            />
          </div>
        </aside>
      </div>

      <div className="schedule-view__bottom">
        <PodcastEpisodesView data={{ title: 'Podcast', maxItems: 6 }} />
      </div>
    </div>
  );
}
